import { ApplicationConfig, APP_INITIALIZER } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { RouteGeneratorService } from './services/route-generator/route-generator.service';
import { PageService } from './services/page/page.service';
import { routes } from './app.routes';

export function initializeRoutes(
  pageService: PageService,
  routeGenerator: RouteGeneratorService,
  router: Router
) {
  return () => {
    return new Promise<void>((resolve) => {
      // Check if user is authenticated
      const user = localStorage.getItem('user');
      if (!user) {
        // Not authenticated, skip route generation
        resolve();
        return;
      }

      // Fetch pages from API
      pageService.fetchPages().subscribe(pages => {
        console.log('🔧 APP CONFIG DEBUG: Pages fetched for route initialization:', pages);

        let allRoutes = [...routes];

        if (pages.length > 0) {
          console.log('🔧 APP CONFIG DEBUG: Generating dynamic routes from', pages.length, 'pages');
          // Generate dynamic routes, passing ALL static routes to avoid conflicts
          const dynamicRoutes = routeGenerator.generateRoutesFromPages(pages, routes);

          // Filter static routes for final combination (exclude ONLY dynamic routes that were created)
          // Keep ALL static routes including custom component routes
          // Exclude the wildcard route (**) as we'll add it at the end
          const filteredStaticRoutes = routes.filter(r =>
            r.path !== '**' &&
            !['fellowships', 'sharing', 'workers', 'training', 'children', 'compassion',
              'activities', 'example-stories', 'community-reach', 'additional-stories',
              'discipleship', 'new-fellowships', 'outreach-persecuted', 'outreach-refugees',
              'outreach-children', 'outreach-ethnic-groups', 'meeting-needs', 'additional-outreach',
              'cam-help', 'obstacles', 'other-agencies', 'partnerships', 'prayer-requests'].includes(r.path || '')
          );

          // DYNAMIC ROUTES FIRST to ensure they take precedence
          allRoutes = [...dynamicRoutes, ...filteredStaticRoutes];
        } else {
          console.log('🔧 APP CONFIG DEBUG: No pages available, using static routes only');
          // Remove wildcard route from routes array as we'll add it at the end
          allRoutes = routes.filter(r => r.path !== '**');
        }

        // Always add wildcard route at the end to catch invalid URLs
        allRoutes.push({
          path: '**',
          redirectTo: 'home'
        });

        console.log('🔧 APP CONFIG DEBUG: Final route configuration:', allRoutes);

        // Update router configuration
        router.resetConfig(allRoutes);
        resolve();
      }, error => {
        console.error('🔧 APP CONFIG DEBUG: Error fetching pages, using static routes only:', error);

        // On error, use static routes with wildcard - exclude ONLY dynamic routes to avoid conflicts
        // Keep ALL static routes including custom component routes (current-ethnic-groups, future-ethnic-groups, photo-upload, submit-survey)
        // Exclude the wildcard route (**) as we'll add it at the end
        const staticRoutes = routes.filter(r =>
          r.path !== '**' &&
          !['fellowships', 'sharing', 'workers', 'training', 'children', 'compassion',
            'activities', 'example-stories', 'community-reach', 'additional-stories',
            'discipleship', 'new-fellowships', 'outreach-persecuted', 'outreach-refugees',
            'outreach-children', 'outreach-ethnic-groups', 'meeting-needs', 'additional-outreach',
            'cam-help', 'obstacles', 'other-agencies', 'partnerships', 'prayer-requests'].includes(r.path || '')
        );

        const allRoutes = [...staticRoutes, {
          path: '**',
          redirectTo: 'home'
        }];

        router.resetConfig(allRoutes);
        resolve();
      });

      // Timeout after 5 seconds if API doesn't respond
      setTimeout(() => {
        resolve();
      }, 5000);
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRoutes,
      deps: [PageService, RouteGeneratorService, Router],
      multi: true
    }
  ]
};